/* eslint-disable @next/next/no-html-link-for-pages */
import React from 'react';

import type { NextPage } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  ArrowRightIcon,
  DocumentTextIcon,
  EyeOffIcon,
  UserGroupIcon
} from '@heroicons/react/outline';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  CoverageSection,
  FadeIn,
  FaqSection,
  Footer,
  GradientBg,
  Header,
  InsuranceTabs,
  PageHeader,
  WhyIsInsuranceImportant
} from '../components';
import { ManagementLiabilitySvg } from '../components/svg/ManagementLiability';

const faqs = [
  {
    question: `What happens if I don't have management liability insurance?`,
    answer:
      'Without management liability insurance, executives, founders and board members may be personally liable for damages resulting from legal claims against them. This can result in significant financial losses and reputational damage for both the individuals and your company. In addition, the absence of management liability insurance may make it more difficult to attract investors and partners who are concerned about risk management and corporate governance.'
  },
  {
    question: 'Why do startups need management liability insurance?',
    answer:
      "Startups need management liability insurance to protect their executives and board members from personal financial loss resulting from lawsuits and legal claims. It also helps protect the company's assets and reputation by providing a defense against claims and promoting good governance practices."
  },
  {
    question: 'Are startups required to have management liability coverage?',
    answer:
      'Venture capital investors often require management liability insurance prior to finalizing due diligence. It shows proper risk management practices and protects your execs and board members from potential liabilities, giving peace of mind to investors and employees. It can also help attract investors and top talent.'
  },
  {
    question: 'How do I show proof I have a management liability policy?',
    answer:
      "Once you've purchased your management liability policy, a Certificate of Insurance (“COI”) will be sent to you and can be presented to any party or investor that requires proof of coverage. You will also receive your management liability policy documentation which can be presented the any third-parties as well."
  }
];

const coverageItems = [
  {
    title: 'Directors & Officers',
    description: `Financial protection for a company's management team against claims related to management decisions. It covers legal defense costs, settlements, and judgments, and protects personal assets in case of lawsuits alleging wrongful acts such as mismanagement or breach of fiduciary duty.`,
    icon: UserGroupIcon
  },
  {
    title: 'Employment Practices Liability',
    description:
      'Protection against claims related to employment practices, such as discrimination or wrongful termination. It covers legal defense costs, settlements, and judgments, and can include coverage for wage disputes and retaliation claims.',
    icon: DocumentTextIcon
  },
  {
    title: 'Fiduciary and Crime',
    description:
      'Financial protection for a company against claims related to employee theft, fraud, or embezzlement. It also protects against claims alleging breach of fiduciary duty or other errors in employee benefits plans. This insurance covers legal defense costs, settlements, and judgments, and is important for companies of all sizes that offer employee benefit plans.',
    icon: EyeOffIcon
  }
];

const ManagementLiability: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="Management liability"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: 'Management liability'
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What happens if I don't have management liability insurance?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Without management liability insurance, executives, founders and board members may be personally liable for damages resulting from legal claims against them. This can result in significant financial losses and reputational damage for both the individuals and your company. In addition, the absence of management liability insurance may make it more difficult to attract investors and partners who are concerned about risk management and corporate governance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Why do startups need management liability insurance?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Startups need management liability insurance to protect their executives and board members from personal financial loss resulting from lawsuits and legal claims. It also helps protect the company's assets and reputation by providing a defense against claims and promoting good governance practices."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Are startups required to have management liability coverage?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Venture capital investors often require management liability insurance prior to finalizing due diligence. It shows proper risk management practices and protects your execs and board members from potential liabilities, giving peace of mind to investors and employees. It can also help attract investors and top talent."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I show proof I have a management liability policy?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Once you've purchased your management liability policy, a Certificate of Insurance (COI) will be sent to you and can be presented to any party or investor that requires proof of coverage. You will also receive your management liability policy documentation which can be presented to any third-parties as well."
                  }
                }
              ]
            })
          }}
        />
      </Head>
      <GradientBg />
      <Header />
      <PageHeader
        title="Management liability"
        image={
          <>
            <ManagementLiabilitySvg className="hidden sm:block mx-auto max-w-4xl w-full mt-8 md:mt-0" />
            <img
              src="/img/mgl.jpg"
              className="sm:hidden w-full max-w-4xl shadow rounded overflow-hidden"
            />
          </>
        }
      />

      {/* Section-1 */}
      <div className="max-w-7xl mx-auto pb-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
        <FadeIn>
          <h2 className="text-5xl text-alt font-extrabold my-7">
            What is <span className="text-primary">management liability</span>{' '}
            insurance?
          </h2>
        </FadeIn>
        <FadeIn>
          <p className="max-w-4xl mt-5 mx-auto text-xl text-gray-500">
            Management liability insurance, also known as directors and officers
            (D&O) insurance, provides financial protection to the company's
            management team against claims related to management decisions. It
            protects against lawsuits alleging wrongful acts such as breach of
            fiduciary duty or mismanagement. Can include additional coverage for
            employment practices, fiduciary, and employee crime. It is purchased
            by companies of all sizes and is particularly important for startups
            and companies with a board of directors.
          </p>
        </FadeIn>
      </div>

      {/* Section-2 */}
      <CoverageSection title="management liability" items={coverageItems} />

      {/* Section-3 */}
      <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:pg-8">
        <div className="text-center">
          <FadeIn>
            <h2 className="text-5xl text-alt font-extrabold my-7">
              Who needs{' '}
              <span className="text-primary">management liability</span>{' '}
              insurance?
            </h2>
          </FadeIn>
          <p
            className="max-w-2xl mt-5 mx-auto text-xl text-gray-500"
            dangerouslySetInnerHTML={{
              __html:
                'Startups and businesses of all sizes that have a board of directors or management team. If you have raised capital from venture capital or intend to you will often be required to show proof of management liability insurance. It is a crucial protection for your founders, management team, cap table, and the future of your business.'
            }}
          />
          <a href="/quote" className="w-auto inline-flex btn-primary mt-4">
            See if you're covered <ArrowRightIcon className="h-4 w-4 inline" />
          </a>
        </div>
        <div className="mt-12 space-y-10 lg:space-y-0 grid lg:grid-cols-2 lg:gap-8">
          <div className="grid grid-flow-row sm:grid-flow-col auto-cols-fr lg:grid-flow-row text-center">
            <div className="relative w-full h-48">
              <Image
                src="/img/insure3.svg"
                aria-hidden="true"
                alt="Small Business Owner icon"
                layout="fill"
              />
            </div>
            <div>
              <p className="mt-4 text-gray-700 text-2xl">
                Small Business Owner
              </p>
              <p className="mt-2 text-base text-gray-500">
                Managing your own business means you have enough on your plate.
              </p>
            </div>
          </div>
          <div className="grid grid-flow-row sm:grid-flow-col auto-cols-fr lg:grid-flow-row text-center">
            <div className="relative w-40 sm:w-48 rounded-lg mx-auto h-40 sm:h-48">
              <Image
                src="/img/startup-owner.jpg"
                aria-hidden="true"
                alt="Startup Owner icon"
                layout="fill"
                className="rounded-xl"
              />
            </div>
            <div>
              <p className="mt-4 text-gray-700 text-2xl">Startup</p>
              <p className="mt-2 text-base text-gray-500">
                Don't let unnecessary business risks make your founder journey
                bumpier than it needs to be.
              </p>
            </div>
          </div>
        </div>
      </div>

      <WhyIsInsuranceImportant>
        <FadeIn>
          <h2 className="text-5xl text-alt font-extrabold my-7">
            Why is <span className="text-primary">management liability</span>{' '}
            important?
          </h2>
        </FadeIn>

        <p className="mt-4 text-lg text-gray-500">
          Regardless of industry or size, a management liability policy can
          protect your startup or business from financial and legal risks
          associated with the company's management team and employees. Venture
          capital and other investors require proof of management liability
          insurance in order to finalize investments and suffice diligence
          requirements.
        </p>
      </WhyIsInsuranceImportant>

      <div className={`bg-white`}>
        <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
          <FadeIn>
            <h2 className="max-w-4xl mx-auto mt-2 text-5xl font-extrabold text-alt">
              What other kinds of{' '}
              <span className="text-primary">business insurance</span> do I
              need?
            </h2>
          </FadeIn>
          <div className="mt-12">
            <InsuranceTabs />
          </div>
        </div>
      </div>

      <FaqSection title="Management liability insurance" faqs={faqs} />

      <BottomGraphic />
      <Footer />
    </>
  );
};

export default ManagementLiability;
